import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useGlobalFilters } from '../../hooks/useGlobalFilters';
import { communityActivityStore } from './CommunityActivityStore';
import { predictionTrendsStore } from "../../Analysis_A/PredictionTrendsStore";
import DataWindowSlider from '../../components/DataWindowSliderPV';
import AllCommunitiesAverageChart from './AllCommunitiesAverageChart';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ScatterController,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './ActivityAnalysis.scss';
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

// Регистрация компонентов Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ScatterController
);

// Определение уровней тревоги и их цветов (синхронизировано с данными API)
const alarmLevels = {
  none: { color: '#E0E0E0', label: 'Очень низкая' }, // Серый
  low: { color: '#A0FFA0', label: 'Низкая' }, // Светло-зеленый
  medium: { color: '#FFFFA0', label: 'Средняя' }, // Желтый
  medium_alt: { color: '#FFE68C', label: 'Высокая' }, // Оранжевый
  high: { color: '#FFB9B9', label: 'Очень Высокая' }, // Светло-красный  
};

function formatNumber(val) {
  if (typeof val === "number") {
    return val.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  }
  if (!isNaN(Number(val))) {
    return Number(val).toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  }
  return val ?? "";
}

function formatDate(date) {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("ru-RU");
}

function getGroupNamesByIds(ids) {
  if (!Array.isArray(ids)) return [];
  return ids.map(id => {
    const group = predictionTrendsStore.groups.find(g => String(g.id) === String(id));
    return group ? group.name : id;
  });
}

const CommunityActivityChart = observer(({ isCommunityActivityTab = false }) => {
  const globalFilters = useGlobalFilters();
  const store = communityActivityStore;
  const [dataWindow, setDataWindow] = React.useState({ start: 0, end: 100 });
  const [aggregation, setAggregation] = React.useState('day'); // 'day' | 'week' | 'month'
  const [viewMode, setViewMode] = React.useState('separate'); // 'separate' | 'average' | 'allCommunities'

  // Функция для обновления окна данных
  const updateWindow = (value) => {
    const newStart = Number(value);
    setDataWindow({ start: newStart, end: newStart + 100 });
  };

  // Функция агрегации данных
  const aggregateData = (data, period) => {
    if (!data || data.length === 0) return [];

    const grouped = {};
    
    data.forEach(item => {
      const date = new Date(item.time);
      let key;
      
      switch (period) {
        case 'week':
          // Начало недели (понедельник)
          const dayOfWeek = date.getDay();
          const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // adjust when day is sunday
          const monday = new Date(date.setDate(diff));
          key = monday.toISOString().split('T')[0];
          break;
        default: // 'day'
          key = date.toISOString().split('T')[0];
          break;
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          time: key,
          activities: [],
          avg_activities: [],
          alarm_levels: []
        };
      }
      
      // Собираем все значения для усреднения
      grouped[key].activities.push(item.activity || 0);
      grouped[key].avg_activities.push(item.avg_activity || 0);
      grouped[key].alarm_levels.push(item.alarm_level);
    });

    // Вычисляем средние значения и определяем преобладающий уровень тревоги
    return Object.values(grouped)
      .map(group => {
        // Среднее значение активности
        const avgActivity = group.activities.reduce((sum, val) => sum + val, 0) / group.activities.length;
        const avgOfAvg = group.avg_activities.reduce((sum, val) => sum + val, 0) / group.avg_activities.length;
        
        // Определяем преобладающий уровень тревоги
        const alarmLevelCounts = {};
        group.alarm_levels.forEach(level => {
          alarmLevelCounts[level] = (alarmLevelCounts[level] || 0) + 1;
        });
        const predominantAlarmLevel = Object.keys(alarmLevelCounts).reduce((a, b) => 
          alarmLevelCounts[a] > alarmLevelCounts[b] ? a : b
        );
        
        return {
          time: group.time,
          activity: avgActivity,
          avg_activity: avgOfAvg,
          alarm_level: predominantAlarmLevel
        };
      })
      .sort((a, b) => new Date(a.time) - new Date(b.time));
  };

  // Автоматически обновляем фильтры и подгружаем данные при изменении глобальных фильтров
  React.useEffect(() => {
    store.setFilters({
      startDate: globalFilters.startDate,
      endDate: globalFilters.endDate,
      selectedGroupIds: globalFilters.selectedGroups,
    });
    store.fetchData();
    // eslint-disable-next-line
  }, [globalFilters.filtersString]);

  // Получаем данные по сообществам
  const dataByCommunity = store.dataByCommunity;
  const averageActivityByCommunity = store.averageActivityByCommunity;

  // Максимальная длина данных для слайдера (берем максимальную среди всех сообществ)
  const maxDataLength = useMemo(() => {
    let maxLength = 0;
    Object.keys(dataByCommunity).forEach(communityId => {
      const communityData = dataByCommunity[communityId];
      const aggregatedData = aggregateData(communityData, aggregation);
      maxLength = Math.max(maxLength, aggregatedData.length);
    });
    return maxLength;
  }, [dataByCommunity, aggregation]);

  // Текст диапазона дат для текущего окна данных
  const dateRangeText = useMemo(() => {
    if (maxDataLength === 0) {
      return 'Нет данных';
    }
    
    const startIndex = Math.min(dataWindow.start, maxDataLength - 1);
    const endIndex = Math.min(dataWindow.end - 1, maxDataLength - 1);
    
    // Получаем даты из первого доступного сообщества
    const firstCommunityId = Object.keys(dataByCommunity)[0];
    if (!firstCommunityId) return 'Нет данных';
    
    const communityData = dataByCommunity[firstCommunityId];
    const aggregatedData = aggregateData(communityData, aggregation);
    
    if (aggregatedData.length === 0) return 'Нет данных';
    
    const startDate = new Date(aggregatedData[startIndex]?.time);
    const endDate = new Date(aggregatedData[endIndex]?.time);
    
    const formatDate = (date) => {
      return !isNaN(date) ? date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }) : 'Недоступно';
    };
    
    return `Показаны данные: ${formatDate(startDate)} - ${formatDate(endDate)}`;
  }, [dataWindow, maxDataLength, dataByCommunity, aggregation]);

  // Сброс окна данных при получении новых данных или изменении агрегации
  React.useEffect(() => {
    setDataWindow({ start: 0, end: 100 });
  }, [maxDataLength, aggregation]);

  // Получаем названия сообществ
  const communityNames = useMemo(() => {
    const names = {};
    Object.keys(dataByCommunity).forEach(communityId => {
      const group = predictionTrendsStore.groups.find(g => String(g.id) === String(communityId));
      names[communityId] = group ? group.name : `Сообщество ${communityId}`;
    });
    return names;
  }, [dataByCommunity]);

  // Данные для отдельных графиков по сообществам
  const communityChartsData = useMemo(() => {
    const charts = {};
    Object.keys(dataByCommunity).forEach(communityId => {
      const communityData = dataByCommunity[communityId];
      const aggregatedData = aggregateData(communityData, aggregation);
      
      // Применяем окно данных для видимых данных
      const startIndex = Math.min(dataWindow.start, aggregatedData.length - 1);
      const endIndex = Math.min(dataWindow.end, aggregatedData.length);
      const visibleData = aggregatedData.slice(startIndex, endIndex);
      
      charts[communityId] = {
        labels: visibleData.map((item) => item.time),
        datasets: [
          {
            type: 'line',
            label: 'Средняя активность',
            data: visibleData.map((item) => item.avg_activity),
            borderColor: '#4a90e2',
            backgroundColor: '#4a90e2',
            fill: false,
            tension: 0.4,
            pointRadius: 0,
          },
          {
            type: 'bar',
            label: 'Активность',
            data: visibleData.map((item) => item.activity),
            backgroundColor: visibleData.map((item) => {
              // Проверяем alarm_level и возвращаем соответствующий цвет
              switch (item.alarm_level) {
                case '#A0FFA0':
                  return alarmLevels.low.color;
                case '#FFE68C':
                  return alarmLevels.medium.color;
                case '#FFFFA0':
                  return alarmLevels.medium_alt.color;
                case '#FFB9B9':
                  return alarmLevels.high.color;
                case '#E0E0E0':
                  return alarmLevels.none.color;
                default:
                  console.warn(`Неизвестный alarm_level: ${item.alarm_level}`);
                  return alarmLevels.none.color;
              }
            }),
            borderColor: 'transparent',
            borderWidth: 0,
            barPercentage: 0.5,
            categoryPercentage: 0.5,
          },
        ],
      };
    });
    return charts;
  }, [dataByCommunity, aggregation, dataWindow]);

  // Данные для графика средних значений
  const averageChartData = useMemo(() => {
    const communityIds = Object.keys(averageActivityByCommunity);
    if (communityIds.length === 0) return null;

    return {
      labels: communityIds.map(id => communityNames[id]),
      datasets: [
        {
          type: 'bar',
          label: 'Средняя активность по сообществам',
          data: communityIds.map(id => averageActivityByCommunity[id].activity),
          backgroundColor: '#4a90e2',
          borderColor: '#4a90e2',
          borderWidth: 1,
          barPercentage: 0.7,
          categoryPercentage: 0.7,
        },
      ],
    };
  }, [averageActivityByCommunity, communityNames]);

  // Настройки графика
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 2.5,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#2d3748',
          font: { size: 14, family: "'Inter', sans-serif" },
          usePointStyle: true,
          pointStyle: 'rect',
          padding: 20,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: (context) => {
            const datasetLabel = context.dataset.label || '';
            const value = context.parsed.y || 0;
            return `${datasetLabel}: ${value}`;
          },
          title: (tooltipItems) => {
            const label = tooltipItems[0].label;
            const date = new Date(label);
            return isNaN(date.getTime()) ? 'Недоступно' : date.toLocaleDateString('ru-RU');
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Время',
          color: '#2d3748',
          font: { size: 14, family: "'Inter', sans-serif", weight: '600' },
        },
        grid: {
          display: false, // Убираем сетку
        },
        border: {
          display: false, // Убираем границы
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 20,
          maxRotation: 45,
          minRotation: 45,
          color: '#4a5568',
          font: { size: 12, family: "'Inter', sans-serif" },
          callback: function (value) {
            const label = this.getLabelForValue(value);
            if (!label || typeof label !== 'string') {
              return 'Недоступно';
            }
            const date = new Date(label);
            if (isNaN(date.getTime())) {
              return 'Недоступно';
            }
            return date.toLocaleDateString('ru-RU');
          },
        },
      },
      y: {
        title: {
          display: true,
          text: 'Активность',
          color: '#2d3748',
          font: { size: 14, family: "'Inter', sans-serif", weight: '600' },
        },
        beginAtZero: true,
        grid: {
          display: false, // Убираем сетку
        },
        border: {
          display: false, // Убираем границы
        },
        ticks: {
          color: '#4a5568',
          font: { size: 12, family: "'Inter', sans-serif" },
        },
      },
    },
    devicePixelRatio: 2,
    elements: {
      line: {
        borderWidth: 3,
        tension: 0.4,
      },
      point: {
        radius: 4,
        hoverRadius: 6,
        borderWidth: 2,
      },
    },
  };

  // Настройки для графика средних значений
  const averageChartOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      x: {
        ...chartOptions.scales.x,
        title: {
          ...chartOptions.scales.x.title,
          text: 'Сообщества',
        },
        ticks: {
          ...chartOptions.scales.x.ticks,
          maxRotation: 0,
          minRotation: 0,
          callback: function (value) {
            const label = this.getLabelForValue(value);
            return label || 'Недоступно';
          },
        },
      },
    },
  };

  // Экспорт в Excel
  const handleExportExcel = async () => {
    const filters = globalFilters.filtersString ? JSON.parse(globalFilters.filtersString) : {};
    const startDate = filters.startDate || "";
    const endDate = filters.endDate || "";
    const groupNames = getGroupNamesByIds(filters.selectedGroups || []);
    const graphTitle = viewMode === 'separate' ? "Активность_по_сообществам" : "Средняя_активность_по_сообществам";
    const fileName = `${graphTitle}_${groupNames.join("_")}_${startDate}_${endDate}.xlsx`;
    
    const ws_data = [["Сообщество", "Date", "activity", "avg_activity"]];
    
    Object.keys(dataByCommunity).forEach(communityId => {
      const communityData = dataByCommunity[communityId];
      const communityName = communityNames[communityId];
      communityData.forEach(d => {
        ws_data.push([
          communityName,
          formatDate(d.time),
          formatNumber(d.activity),
          formatNumber(d.avg_activity)
        ]);
      });
    });
    
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Данные");
    XLSX.writeFile(wb, fileName);
  };

  // Экспорт в PDF
  const handleExportPDF = async () => {
    const filters = globalFilters.filtersString ? JSON.parse(globalFilters.filtersString) : {};
    const startDate = filters.startDate || "";
    const endDate = filters.endDate || "";
    const groupNames = getGroupNamesByIds(filters.selectedGroups || []);
    const graphTitle = viewMode === 'separate' ? "Активность_по_сообществам" : "Средняя_активность_по_сообществам";
    const fileName = `${graphTitle}_${groupNames.join("_")}_${startDate}_${endDate}.pdf`;
    
    const input = document.querySelector("#community-activity-chart-container");
    if (!input) { alert("График не найден для экспорта!"); return; }
    const canvas = await html2canvas(input);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");
    pdf.addImage(imgData, "PNG", 10, 10, 270, 80);
    
    // Добавляем таблицу с данными
    const tableData = [];
    Object.keys(dataByCommunity).forEach(communityId => {
      const communityData = dataByCommunity[communityId];
      const communityName = communityNames[communityId];
      communityData.forEach(d => {
        tableData.push([
          communityName,
          formatDate(d.time),
          formatNumber(d.activity),
          formatNumber(d.avg_activity)
        ]);
      });
    });
    
    autoTable(pdf, {
      startY: 100,
      head: [["Сообщество", "Date", "activity", "avg_activity"]],
      body: tableData,
      styles: { font: "helvetica", fontSize: 10 }
    });
    pdf.save(fileName);
  };

  // Рендеринг компонента
  return (
    <div className="activity-analysis">
      <div className="activity-analysis-container">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16, width: '100%', flex: '1 1 100%' }}>
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Селектор режима просмотра */}
            <select 
              value={viewMode} 
              onChange={e => setViewMode(e.target.value)} 
              style={{ 
                fontSize: 16, 
                padding: '6px 12px', 
                borderRadius: 8, 
                border: '1px solid #1976d2', 
                color: '#1976d2', 
                fontWeight: 500,
                backgroundColor: '#fff'
              }}
            >
              <option value="separate">По сообществам</option>
              <option value="average">Средние значения</option>
              <option value="allCommunities">Средние линии всех сообществ</option>
            </select>
            
            {/* Селектор агрегации показываем только в режиме "По сообществам" */}
            {viewMode === 'separate' && (
              <select 
                value={aggregation} 
                onChange={e => setAggregation(e.target.value)} 
                style={{ 
                  fontSize: 16, 
                  padding: '6px 12px', 
                  borderRadius: 8, 
                  border: '1px solid #1976d2', 
                  color: '#1976d2', 
                  fontWeight: 500,
                  backgroundColor: '#fff'
                }}
              >
                <option value="day">По дням</option>
                <option value="week">По неделям</option>
              </select>
            )}
          </div>
          
          {/* Кнопки справа под графиком */}
          <div className="activity-analysis-content-box" style={{
            width: '100%'
          }}>
            {/* Кастомная легенда для уровней тревоги */}
            {viewMode === 'separate' && (
              <div className="activity-analysis-custom-legend" style={{ marginBottom: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {Object.entries(alarmLevels).map(([key, { color, label }]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '16px',
                        height: '16px',
                        backgroundColor: color,
                        marginRight: '8px',
                        borderRadius: '4px',
                      }}
                    />
                    <span style={{ color: '#2d3748', fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Слайдер прокрутки данных */}
            {maxDataLength > 100 && !store.loading && !store.error && viewMode === 'separate' && (
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
                <DataWindowSlider
                  dataWindow={dataWindow}
                  maxDataLength={maxDataLength}
                  updateWindow={updateWindow}
                  dateRangeText={dateRangeText}
                />
              </div>
            )}

            {/* График */}
            <div className="activity-analysis-chart-wrapper" id="community-activity-chart-container" style={{
              height: 'auto',
              width: '100%',
              borderRadius: 12,
              padding: 0,
              position: 'relative'
            }}>
              {store.loading ? (
                <div className="activity-analysis-no-data">Загрузка...</div>
              ) : store.error ? (
                <div className="activity-analysis-no-data">Ошибка: {store.error.message}</div>
              ) : viewMode === 'allCommunities' ? (
                <AllCommunitiesAverageChart />
              ) : viewMode === 'separate' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                  {Object.keys(communityChartsData).map(communityId => (
                    <div key={communityId} style={{ marginBottom: '32px' }}>
                      <h3 style={{ 
                        textAlign: 'center', 
                        marginBottom: '16px', 
                        color: '#2d3748',
                        fontSize: '18px',
                        fontWeight: '600'
                      }}>
                        {communityNames[communityId]}
                      </h3>
                      <div style={{ height: '400px', width: '100%' }}>
                        <Bar data={communityChartsData[communityId]} options={chartOptions} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : averageChartData ? (
                <div style={{ height: '60vh', width: '100%' }}>
                  <Bar data={averageChartData} options={averageChartOptions} />
                </div>
              ) : globalFilters.selectedGroups.length ? (
                <div className="activity-analysis-no-data">К сожалению на данный момент для выбранных групп нет данных, попробуйте выбрать другие сообщества</div>
              ) : (
                <div className="activity-analysis-no-data">Выберите сообщества в верхней панели для отображения графика</div>
              )}
            </div>
            
            {!isCommunityActivityTab && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24, marginRight: 24 }}>
                <button className="activity-analysis-export-btn" onClick={handleExportExcel}>Экспорт в Excel</button>
                <button className="activity-analysis-export-btn" onClick={handleExportPDF}>Экспорт в PDF</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default CommunityActivityChart;
